import { DoneInvokeEvent } from "xstate";
import { LinkFn, NavigateFn } from "../../../components/Link/Link";
import { RangeType } from "../../../constants/types";
import { MealType } from "../../../types/graphql-global-types";
import { Locale } from "../../../types/Locale";
import { DayKey } from "../../../types/MealPlan";
import { MealPlanner_GetRecipeQuery_recipe } from "../../../types/MealPlanner_GetRecipeQuery";
import { MealPlanner_GetTagsQuery_listTags_tags } from "../../../types/MealPlanner_GetTagsQuery";
import { MealPlanner_MealPlan, MealPlanner_MealPlan_schedule } from "../../../types/MealPlanner_MealPlan";
import { MealPlanner_MergedIngredient } from "../../../types/MealPlanner_MergedIngredient";
import { MealPlanner_Recipe } from "../../../types/MealPlanner_Recipe";
import {
  MealPlanner_RecipeWithServings,
  MealPlanner_RecipeWithServings_recipe,
} from "../../../types/MealPlanner_RecipeWithServings";
import { Weekday } from "../../../utils/date";
import { IngredientUnit } from "../../../utils/units";
import { useMealPlanMachine } from "./useMealPlanMachine";

export interface Context {
  slug: string;
  mealPlan?: MealPlanner_MealPlan;
  formattedMealPlan?: FormattedMealPlan;
  currentDay?: DayKey;
  currentMeal?: MealType;
  currentRecipe?: MealPlanner_RecipeWithServings;
  currentRecipeIndex?: number;
  userId?: string;
  firebaseUserId?: string;
  isPremium: boolean;
  isMenuOpen: boolean;
  navigate: NavigateFn;
  history?: string;
  link: LinkFn;
  //favorites tab in recipeSelector
  favoriteRecipes: MealPlanner_Recipe[];
  //search tab in recipeSelector
  searchInput: string;
  searchResult?: MealPlanner_Recipe[];
  allTags: Array<MealPlanner_GetTagsQuery_listTags_tags>;
  tagInput: Array<string>;
  chosenTags: Array<MealPlanner_GetTagsQuery_listTags_tags>;
  //suggestion tab in recipeSelector
  recipeSuggestion: MealPlanner_Recipe[];
  //for recipePreview
  fullRecipe?: MealPlanner_GetRecipeQuery_recipe;

  /** Holds the message from the last operation that needs to be acknowledged by the user. */
  confirmationMessage?: MealPlannerConfirmationMessage;

  /** Holds the error from the last operation. */
  error?: DDError<MealPlannerErrorCode>;

  /** Holds which measurement system to use when displaying amounts (e.g. ingredient quantities). */
  ingredientUnit: IngredientUnit;

  /** Holds which print target area is the active one. */
  printTarget?: PrintTarget;

  /** Indicates if the user agent has native sharing capabilities. */
  canShare: boolean;

  /** Indicates if the user agent supports copying text to the clipboard. */
  canCopyToClipboard: boolean;

  /** Indicates if there's a request to immediately edit the meal plan. */
  isEditRequested: boolean;

  locale: Locale;

  /** Indicates which serving sizes are allowed for meal plans and days. */
  allowedServings: number[];

  /** Contains the local ingredients list, considering current recipes and serving sizes. */
  ingredientsList?: MealPlanner_MergedIngredient[];

  originalEvent?: Events;
  /** Holds which day the current recipe will be copied to */
  copyToDay?: DayKey;

  /** Holds which meal the current recipe will be copied to */
  copyToMeal?: MealType;

  // Holds the number for page when fetching recipes in search
  pageInputForSearchRecipes: number;

  //load more button in search tab in select recipe view
  showLoadMoreButton: boolean;

  //recipe of interest when user chooses from recipes in recipe selector
  focusedRecipe?: MealPlanner_RecipeWithServings_recipe;

  //which tab to go back to after user has been seeing the recipe's nutrition in the recipeSelector
  selectRecipeHistory?: "suggestions" | "favorites" | "search";

  selectedSatietyRange: RangeType[];
  selectedCookTimeLimit: RangeType[];
}

export type Events =
  | UserStateChangeEvent
  | ToggleDayMenuEvent
  | StartMealPlanEvent
  | StopMealPlanEvent
  | CopyMealPlanEvent
  | ConfirmMealPlanCopyEvent
  | EditMealPlanEvent
  | ConfirmMealPlanEditEvent
  | ShareMealPlanEvent
  | UnshareMealPlanEvent
  | ShowMealPlanMenuEvent
  | ShowMealPlanInfoEvent
  | ShowMealPlanNutritionalInfoEvent
  | ShowMealPlanIngredientsListEvent
  | AddRecipesToShoppingListEvent
  | CloseViewEvent
  | GoToPreviousViewEvent
  | ConfirmMessageEvent
  | ConfirmErrorEvent
  | DoneInvokeEvent<boolean>
  | DoneInvokeEvent<DDError<MealPlannerErrorCode>>
  | DoneInvokeEvent<MealPlanner_GetRecipeQuery_recipe>
  | ToggleRecipeMenuEvent
  | AddRecipeEvent
  | RemoveRecipeEvent
  | ChangeRecipeEvent
  | GoBackToDayMenuEvent
  | GoBackToRecipeMenuEvent
  | SuggestionsTabEvent
  | FavoritesTabEvent
  | SearchTabEvent
  | AddBreakfastEvent
  | AddLunchEvent
  | AddDinnerEvent
  | SearchInputChangeEvent
  | ClearSearchInputEvent
  | TagChangeEvent
  | ToggleTagView
  | RemoveTagEvent
  | AddTagEvent
  | GenerateNewSuggestionEvent
  | ShowRecipePreviewEvent
  | UnfavoriteRecipeEvent
  | FavoriteRecipeEvent
  | ShareRecipeEvent
  | ShowDayNutritionalInfoEvent
  | ShareContentEvent
  | CopyToClipboardEvent
  | DeleteMealPlanEvent
  | UpdateMealPlanServingsEvent
  | UpdateMealPlanDayServingsEvent
  | UpdateMealPlanRecipeServingsEvent
  | ConfirmActionEvent
  | CancelActionEvent
  | CopyRecipeEvent
  | SelectCopyToDayEvent
  | SelectCopyToMealEvent
  | LoadMoreRecipesEvent
  | SetCurrentDayEvent
  | ToggleRecipeCardMenuEvent
  | ShowRecipeCardNutritionalInforEvent
  | ToggleIngredientUnitEvent
  | SetIngredientUnitEvent
  | ChangeCookTimeLimitEvent
  | SetSatietyScoreRangeEvent
  | RemoveCooktimeRangeTagEvent
  | RemoveSatietyRangeTagEvent;

export type SetIngredientUnitEvent = {
  type: "SET_INGREDIENT_UNIT";
  ingredientUnit: IngredientUnit;
};

export type ToggleIngredientUnitEvent = {
  type: "TOGGLE_INGREDIENT_UNIT";
  ingredientUnit: IngredientUnit;
};

export type ShowRecipeCardNutritionalInforEvent = {
  type: "SHOW_RECIPE_CARD_NUTRITIONAL_INFO";
  recipe?: MealPlanner_RecipeWithServings_recipe;
};

export type ToggleRecipeCardMenuEvent = {
  type: "TOGGLE_RECIPE_CARD_MENU";
  recipe?: MealPlanner_RecipeWithServings_recipe;
  history?: "suggestions" | "favorites" | "search";
};

type SetCurrentDayEvent = { type: "SET_CURRENT_DAY" };

type LoadMoreRecipesEvent = { type: "LOAD_MORE_RECIPES" };

export type SelectCopyToDayEvent = {
  type: "SELECT_COPY_TO_DAY";
  day: DayKey;
};
export type SelectCopyToMealEvent = {
  type: "SELECT_COPY_TO_MEAL";
  meal: MealType;
};
export type UserStateChangeEvent = {
  type: "USER_STATE_CHANGE";
  id?: string;
  firebaseUserId?: string;
  premium: boolean;
};

export type ToggleDayMenuEvent = {
  type: "TOGGLE_DAY_MENU";
  day?: DayKey;
  history?: string;
};
export type ToggleRecipeMenuEvent = {
  type: "TOGGLE_RECIPE_MENU";
  meal?: MealType;
  recipe?: MealPlanner_RecipeWithServings;
  day?: string;
  history?: "recipeMenu" | "dayMenu";
  index?: number;
};
interface StartMealPlanEvent {
  type: "START_MEAL_PLAN";
}

interface StopMealPlanEvent {
  type: "STOP_MEAL_PLAN";
}

interface CopyMealPlanEvent {
  type: "COPY_MEAL_PLAN";
}

export interface ConfirmMealPlanCopyEvent {
  type: "CONFIRM_MEAL_PLAN_COPY";
  title?: string;
  description?: string;
}

interface EditMealPlanEvent {
  type: "EDIT_MEAL_PLAN";
}

export interface ConfirmMealPlanEditEvent {
  type: "CONFIRM_MEAL_PLAN_EDIT";
  title?: string;
  description?: string;
}

export interface UpdateMealPlanServingsEvent {
  type: "UPDATE_MEAL_PLAN_SERVINGS";
  servings: number;
}

export interface UpdateMealPlanDayServingsEvent {
  type: "UPDATE_MEAL_PLAN_DAY_SERVINGS";
  day: DayKey;
  servings: number;
}

export interface UpdateMealPlanRecipeServingsEvent {
  type: "UPDATE_MEAL_PLAN_RECIPE_SERVINGS";
  day: DayKey;
  meal: MealType;
  recipe: MealPlanner_RecipeWithServings_recipe;
  index: number;
  servings: number;
}

interface ShareMealPlanEvent {
  type: "SHARE_MEAL_PLAN";
}

interface UnshareMealPlanEvent {
  type: "UNSHARE_MEAL_PLAN";
}

interface DeleteMealPlanEvent {
  type: "DELETE_MEAL_PLAN";
}

interface ShowMealPlanMenuEvent {
  type: "SHOW_MEAL_PLAN_MENU";
}

interface ShowMealPlanInfoEvent {
  type: "SHOW_MEAL_PLAN_INFO";
}

interface ShowMealPlanNutritionalInfoEvent {
  type: "SHOW_MEAL_PLAN_NUTRITIONAL_INFO";
}

interface ShowMealPlanIngredientsListEvent {
  type: "SHOW_MEAL_PLAN_INGREDIENTS_LIST";
}

interface CloseViewEvent {
  type: "CLOSE_VIEW";
}

interface GoToPreviousViewEvent {
  type: "GO_TO_PREVIOUS_VIEW";
}

interface ConfirmMessageEvent {
  type: "CONFIRM_MESSAGE";
}

interface ConfirmErrorEvent {
  type: "CONFIRM_ERROR";
}

export type RemoveRecipeEvent = {
  type: "REMOVE_RECIPE";
  day: DayKey;
  meal: MealType;
  index: number;
};

export type AddRecipeEvent = {
  type: "ADD_RECIPE";
  day: DayKey;
  meal: MealType;
  recipeId: string;
  servings: number;
  index?: number;
};

type ChangeRecipeEvent = {
  type: "CHANGE_RECIPE";
};

type GoBackToRecipeMenuEvent = {
  type: "GO_TO_RECIPE_MENU_IDLE";
};
type GoBackToDayMenuEvent = {
  type: "GO_TO_DAY_MENU_IDLE";
};

type SuggestionsTabEvent = {
  type: "SHOW_SUGGESTIONS_TAB";
};
type FavoritesTabEvent = {
  type: "SHOW_FAVORITES_TAB";
};
type SearchTabEvent = {
  type: "SHOW_SEARCH_TAB";
};
export type AddBreakfastEvent = {
  type: "ADD_BREAKFAST";
  meal: MealType.BREAKFAST;
};
export type AddLunchEvent = {
  type: "ADD_LUNCH";
  meal: MealType.LUNCH;
};
export type AddDinnerEvent = {
  type: "ADD_DINNER";
  meal: MealType.DINNER;
};

type ShowDayNutritionalInfoEvent = {
  type: "SHOW_DAY_NUTRITIONAL_INFO";
};

export interface ShareContentEvent {
  type: "SHARE";
  title: string;
  url: string;
}

export interface CopyToClipboardEvent {
  type: "COPY_TO_CLIPBOARD";
  value: string;
}

export type SearchInputChangeEvent = { type: "SEARCH_INPUT_CHANGE"; input: string };

type ClearSearchInputEvent = { type: "CLEAR_SEARCH_INPUT" };

export type TagChangeEvent = { type: "TAG_CHANGE"; tags: Array<string> };

type ToggleTagView = { type: "TOGGLE_TAG_VIEW" };

export type RemoveTagEvent = { type: "REMOVE_TAG"; tag: MealPlanner_GetTagsQuery_listTags_tags };
export type AddTagEvent = { type: "ADD_TAG"; tag: MealPlanner_GetTagsQuery_listTags_tags };

export type ChangeCookTimeLimitEvent = {
  type: "SET_COOK_TIME_LIMIT";
  selectedCookTimeRange: RangeType[];
};

export type SetSatietyScoreRangeEvent = {
  type: "SET_SATIETY_SCORE_RANGE";
  selectedSatietyRange: RangeType[];
};

export type RemoveCooktimeRangeTagEvent = {
  type: "REMOVE_COOKTIME_RANGE_TAG";
  tag: RangeType;
};

export type RemoveSatietyRangeTagEvent = {
  type: "REMOVE_SATIETY_RANGE_TAG";
  tag: RangeType;
};

type GenerateNewSuggestionEvent = { type: "GENERATE_NEW_SUGGESTION" };

type ShowRecipePreviewEvent = {
  type: "SHOW_RECIPE_PREVIEW";
  day?: string;
  recipe?: MealPlanner_RecipeWithServings;
  meal?: MealType;
  index?: number;
};

type FavoriteRecipeEvent = { type: "FAVORITE_RECIPE" };
type UnfavoriteRecipeEvent = { type: "UNFAVORITE_RECIPE" };

type ShareRecipeEvent = { type: "SHARE_RECIPE" };

interface ConfirmActionEvent {
  type: "CONFIRM_ACTION";
}

interface CancelActionEvent {
  type: "CANCEL_ACTION";
}
type CopyRecipeEvent = {
  type: "COPY_RECIPE_TO";
};

export type FormattedMealPlan = Array<FormattedMealPlanDay>;

export type FormattedMealPlanDay = {
  weekday: DayKey;
  mealplanDay: MealPlanner_MealPlan_schedule;
};

export interface NutritionalInfo {
  netCarbs: NutritionalStat;
  fat: NutritionalStat;
  protein: NutritionalStat;

  fiber?: number;
  totalCarbs: number;
  calories: number;
}

interface NutritionalStat {
  amount: number;
  percentage: number;
}

export interface IngredientsList {
  sections: IngredientsListSection[];
}

export interface IngredientsListSection {
  name: string;
  ingredients: IngredientsListIngredient[];
}

export interface IngredientsListIngredient {
  id: string;
  name: string;
  values: Partial<Record<IngredientUnit, IngredientsListIngredientValue>>;
}

export interface IngredientsListIngredientValue {
  amount: string;
  unit: string;
}

export interface RecipeOccurrence {
  recipe: MealPlanner_Recipe;
  servings: number;
  meals: RecipeOccurrenceMeal[];
}

interface RecipeOccurrenceMeal {
  weekday: Weekday;
  meal: MealType;
}

export type MealPlanMachineState = ReturnType<typeof useMealPlanMachine>[0];
export type MealPlanMachineSend = ReturnType<typeof useMealPlanMachine>[1];

export interface ScreenProps {
  state: MealPlanMachineState;
  send: MealPlanMachineSend;
}

export enum EnergyLevel {
  HIGH_PROTEIN_KETO = "HIGH_PROTEIN_KETO",
  HIGH_PROTEIN_LOW_CARB = "HIGH_PROTEIN_LOW_CARB",
  HIGH_PROTEIN_LIBERAL = "HIGH_PROTEIN_LIBERAL",
  KETO = "KETO",
  LOW_CARB = "LOW_CARB",
  LIBERAL = "LIBERAL",
}

export enum PrintTarget {
  INGREDIENTS_LIST = "INGREDIENTS_LIST",
}

export type AddRecipesToShoppingListEvent = {
  type: "ADD_RECIPES_TO_SHOPPING_LIST";
  recipeOrMealPlanDays: MealPlanner_RecipeWithServings | DayKey[];
};

export interface Message {
  title: string;
  description?: string;
}

export enum MealPlannerConfirmationMessage {
  MEAL_PLAN_STARTED = "MEAL_PLAN_STARTED",
  MEAL_PLAN_STOPPED = "MEAL_PLAN_STOPPED",
  MEAL_PLAN_SHARED = "MEAL_PLAN_SHARED",
  MEAL_PLAN_UNSHARED = "MEAL_PLAN_UNSHARED",
  RECIPE_SHARED = "RECIPE_SHARED",
  MEALPLAN_DELETED = "MEALPLAN_DELETED",
  RECIPES_ADDED_TO_SHOPPING_LIST = "RECIPES_ADDED_TO_SHOPPING_LIST",
}

export enum MealPlannerErrorCode {
  /** The user needs a premium membership to access this feature. */
  PREMIUM_MEMBERSHIP_REQUIRED = "PREMIUM_MEMBERSHIP_REQUIRED",

  /** The requested meal plan does not exist. */
  MEAL_PLAN_NOT_FOUND = "MEAL_PLAN_NOT_FOUND",

  /**
   * A server error occurred when loading the meal plan OR favorite recipe of the user. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_LOAD_INITIAL_DATA = "FAILED_TO_LOAD_INITIAL_DATA",

  /** The user can not start the meal plan. This error is originated client-side. */
  CAN_NOT_START_MEAL_PLAN = "CAN_NOT_START_MEAL_PLAN",

  /**
   * A server error occurred when starting the meal plan. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_START_MEAL_PLAN = "FAILED_TO_START_MEAL_PLAN",

  /** The user can not stop the meal plan. This error is originated client-side. */
  CAN_NOT_STOP_MEAL_PLAN = "CAN_NOT_STOP_MEAL_PLAN",

  /**
   * A server error occurred when stopping the meal plan. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_STOP_MEAL_PLAN = "FAILED_TO_STOP_MEAL_PLAN",

  /** The user can not share the meal plan. This error is originated client-side. */
  CAN_NOT_SHARE_MEAL_PLAN = "CAN_NOT_SHARE_MEAL_PLAN",

  /**
   * A server error occurred when sharing the meal plan. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_SHARE_MEAL_PLAN = "FAILED_TO_SHARE_MEAL_PLAN",

  /** The user can not unshare the meal plan. This error is originated client-side. */
  CAN_NOT_UNSHARE_MEAL_PLAN = "CAN_NOT_UNSHARE_MEAL_PLAN",

  /**
   * A server error occurred when unsharing the meal plan. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_UNSHARE_MEAL_PLAN = "FAILED_TO_UNSHARE_MEAL_PLAN",

  /** The user can not copy the meal plan. This error is originated client-side. */
  CAN_NOT_COPY_MEAL_PLAN = "CAN_NOT_COPY_MEAL_PLAN",

  /**
   * A server error occurred when copying the meal plan. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_COPY_MEAL_PLAN = "FAILED_TO_COPY_MEAL_PLAN",

  /** The user can not edit the meal plan. This error is originated client-side. */
  CAN_NOT_EDIT_MEAL_PLAN = "CAN_NOT_EDIT_MEAL_PLAN",

  /**
   * A server error occurred when editing the meal plan. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_EDIT_MEAL_PLAN = "FAILED_TO_EDIT_MEAL_PLAN",

  /** The user can not add a recipe to the meal plan. This error is originated client-side. */
  CAN_NOT_ADD_RECIPE = "CAN_NOT_ADD_RECIPE",

  /**
   * A server error occurred when adding a recipe to the meal plan. This is a generic catch-all
   * error code for all server-originating errors.
   */
  FAILED_TO_ADD_RECIPE = "FAILED_TO_ADD_RECIPE",

  /** The user can not remove a recipe to the meal plan. This error is originated client-side. */
  CAN_NOT_REMOVE_RECIPE = "CAN_NOT_REMOVE_RECIPE",

  /**
   * A server error occurred when removing a recipe from the meal plan. This is a generic catch-all
   * error code for all server-originating errors.
   */
  FAILED_TO_REMOVE_RECIPE = "FAILED_TO_REMOVE_RECIPE",

  /**
   * A server error occurred when loading a recipe suggestion. This is a generic catch-all error
   * code for all server-originating errors.
   */
  FAILED_TO_LOAD_RECIPE_SUGGESTION = "FAILED_TO_LOAD_RECIPE_SUGGESTION",

  /**
   * A server error occurred when loading favorite recipes. This is a generic catch-all error code
   * for all server-originating errors.
   */
  FAILED_TO_LOAD_FAVORITE_RECIPES = "FAILED_TO_LOAD_FAVORITE_RECIPES",

  /**
   * A server error occurred when loading tags. This is a generic catch-all error code for all
   * server-originating errors.
   */
  FAILED_TO_LOAD_TAGS = "FAILED_TO_LOAD_TAGS",

  /**
   * A server error occurred when searching for recipes. This is a generic catch-all error code for
   * all server-originating errors.
   */
  FAILED_TO_SEARCH_RECIPES = "FAILED_TO_SEARCH_RECIPES",

  /**
   * A server error occurred when loading a recipe. This is a generic catch-all error code for
   * all server-originating errors.
   */
  FAILED_TO_LOAD_FULL_RECIPE = "FAILED_TO_LOAD_FULL_RECIPE",

  /**
   * A server error occurred when favoriting a recipe. This is a generic catch-all error code for
   * all server-originating errors.
   */

  FAILED_TO_FAVORITE_RECIPE = "FAILED_TO_FAVORITE_RECIPE",

  /**
   * A server error occurred when unfavoriting a recipe. This is a generic catch-all error code for
   * all server-originating errors.
   */
  FAILED_TO_UNFAVORITE_RECIPE = "FAILED_TO_UNFAVORITE_RECIPE",

  /** The user can not natively share content. This error is originated client-side. */
  CAN_NOT_SHARE = "CAN_NOT_SHARE",

  /** The user can not copy text to the clipboard. This error is originated client-side. */
  CAN_NOT_COPY_TO_CLIPBOARD = "CAN_NOT_COPY_TO_CLIPBOARD",

  /** The user can not delete the meal plan.This error is originated client-side. */
  CAN_NOT_DELETE_MEALPLAN = "CAN_NOT_DELETE_MEALPLAN",

  /** The user can not add recipes to the shopping list because the meal does not contain any. */
  CAN_NOT_ADD_RECIPES_SHOPPING_LIST = "CAN_NOT_ADD_RECIPES_SHOPPING_LIST",

  /**
   * A server error occurred when deleting a mealplan. This is a generic catch-all error code for
   * all server-originating errors.
   */ FAILED_TO_DELETE_MEALPLAN = "FAILED_TO_DELETE_MEALPLAN",

  /** The user can not add more than 1 recipe per meal. This error is originated client-side. */
  TOO_MANY_RECIPES = "TOO_MANY_RECIPES",

  CAN_NOT_UPDATE_MEAL_PLAN_SERVINGS = "CAN_NOT_UPDATE_MEAL_PLAN_SERVINGS",
  FAILED_TO_UPDATE_MEAL_PLAN_SERVINGS = "FAILED_TO_UPDATE_MEAL_PLAN_SERVINGS",

  CAN_NOT_UPDATE_MEAL_PLAN_DAY_SERVINGS = "CAN_NOT_UPDATE_DAY_SERVINGS",
  FAILED_TO_UPDATE_MEAL_PLAN_DAY_SERVINGS = "FAILED_TO_UPDATE_DAY_SERVINGS",

  CAN_NOT_UPDATE_MEAL_PLAN_RECIPE_SERVINGS = "CAN_NOT_UPDATE_MEAL_PLAN_RECIPE_SERVINGS",
  FAILED_TO_UPDATE_MEAL_PLAN_RECIPE_SERVINGS = "FAILED_TO_UPDATE_MEAL_PLAN_RECIPE_SERVINGS",
}

// TODO: Move this error to a more globally available place.
export class DDError<ErrorCode extends string> extends Error {
  public readonly code: ErrorCode;
  public readonly reason?: Error;

  public constructor(code: ErrorCode, message?: string, reason?: Error) {
    super(message);
    this.code = code;
    this.reason = reason;
  }
}

export type FirebaseShoppingListRecipe = {
  id: string;
  uuid: string;
  type: "recipe";
  servings: number;
};

export type FirebaseShoppingListPayload = {
  version: string;
  list: {
    ingredients: object[];
    customIngredients: object[];
    items: FirebaseShoppingListRecipe[];
  };
};
